import { useId, useMemo, useRef, useState } from "react";
import SelectView from "./SelectView";
import { useLanguageContext } from "../../../context/LanguageContext";
import useCustomEffect from "../../../hooks/useCustomEffect";
import StringHelper from "../../../helpers/StringHelper";

// The time in ms after which the type-ahead of a non-searchable select is reset
const TYPE_AHEAD_TIMEOUT = 500;

/**
 * This method will return the portal element the options are rendered in, and create it if it doesn't exist yet
 * @returns {HTMLElement}
 */
function getOptionsPortal() {
  let portal = document.getElementById("select-options-portal");
  if (!portal) {
    portal = document.createElement("div");
    portal.id = "select-options-portal";
    document.body.appendChild(portal);
  }
  return portal;
}

/**
 * This method will check if the given key event is a printable character without modifier keys
 * @param {event} e
 * @returns {boolean}
 */
function isPrintableKey(e) {
  return e.key?.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
}

function Select({
  value,
  options = [],
  nullable = false,
  searchable = null,
  onSearchChanged = (val) => {},
  unfocusOnChange = true,
  disabled = false,
  sort = true,
  onChange = (val) => {},
  as,
  hideCaret,
  valueVisual,
  placeholder,
  title,
  style,
  onKeyDown: onCustomKeyDown,
}) {
  const { translate } = useLanguageContext();
  const id = useId();
  const [focused, setFocused] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [autoSearchable, setAutoSearchable] = useState(false);
  const selectRef = useRef(null);
  const optionsRef = useRef(null);
  const searchRef = useRef(null);
  const typeAheadRef = useRef({ text: "", timeout: null });

  // Sort the options by label and add the null option
  const sortedOptions = useMemo(() => {
    const result = sort
      ? [...options].sort((a, b) => {
          return `${a.label}`.localeCompare(`${b.label}`);
        })
      : [...options];

    if (nullable && !result.some((option) => option.value === null)) {
      result.unshift({
        value: null,
        label: placeholder ?? translate("eva.main.general.select"),
      });
    }

    return result;
  }, [options, sort, nullable, placeholder, translate]);

  // Get the selected option
  const selectedOption = useMemo(() => {
    return (
      sortedOptions.find((option) => option.value === value) ??
      (nullable ? sortedOptions[0] : null)
    );
  }, [sortedOptions, value, nullable]);

  // Filter the options by search
  const filteredOptions = useMemo(() => {
    return search
      ? sortedOptions.filter((option) =>
          StringHelper.matchesSearch(option.label, search),
        )
      : sortedOptions;
  }, [search, sortedOptions]);

  // Use the given searchable value, or the one determined when the select was opened
  const isSearchable = searchable ?? autoSearchable;

  /**
   * UseEffect for closing the options when the select is clicked outside of it
   */
  useCustomEffect(() => {
    if (!focused) return;
    window.addEventListener("mousedown", onWindowMouseDown);
    return () => {
      window.removeEventListener("mousedown", onWindowMouseDown);
    };
  }, [focused]);

  /**
   * UseEffect for focusing the search input when the select is opened
   */
  useCustomEffect(() => {
    if (!focused) return;
    const frame = requestAnimationFrame(() => {
      const input = searchRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange?.(input.value.length, input.value.length);
    });
    return () => cancelAnimationFrame(frame);
  }, [focused]);

  /**
   * UseEffect for positioning the options while the select is opened
   */
  useCustomEffect(() => {
    return activateOptionPositioning();
  }, [focused]);

  /**
   * UseEffect for scrolling the highlighted option into view
   */
  useCustomEffect(() => {
    if (!focused || highlightedIndex < 0) return;
    document
      .getElementById(getOptionId(highlightedIndex))
      ?.scrollIntoView?.({ block: "nearest" });
  }, [focused, highlightedIndex]);

  /**
   * UseEffect for closing the options when the select becomes disabled
   */
  useCustomEffect(() => {
    if (disabled && focused) updateFocused(false);
  }, [disabled]);

  /**
   * UseEffect for clearing the type-ahead timeout when the component is unmounted
   */
  useCustomEffect(() => {
    const typeAhead = typeAheadRef.current;
    return () => clearTimeout(typeAhead.timeout);
  }, []);

  /**
   * This method will return the DOM id of the option with the given index
   * @param {number} index
   * @returns {string}
   */
  function getOptionId(index) {
    return `${id}-option-${index}`;
  }

  /**
   * This method will check if the option with the given index can be selected
   * @param {array} list
   * @param {number} index
   * @returns {boolean}
   */
  function isSelectable(list, index) {
    return list[index] !== undefined && !list[index].disabled;
  }

  /**
   * This method will return the index of the first selectable option, starting at the given index and moving in the given direction
   * @param {array} list
   * @param {number} start
   * @param {number} direction 1 or -1
   * @returns {number} -1 if there is no selectable option
   */
  function findSelectableIndex(list, start, direction) {
    for (let i = start; i >= 0 && i < list.length; i += direction) {
      if (isSelectable(list, i)) return i;
    }
    return -1;
  }

  /**
   * This method will return the index that should be highlighted when the options are shown
   * @param {array} list
   * @returns {number}
   */
  function getInitialHighlightIndex(list) {
    const selectedIndex = list.findIndex(
      (option) => option.value === selectedOption?.value,
    );
    return isSelectable(list, selectedIndex)
      ? selectedIndex
      : findSelectableIndex(list, 0, 1);
  }

  /**
   * This method will close the options when the user clicks outside of the select
   * @param {event} e
   */
  function onWindowMouseDown(e) {
    if (
      optionsRef.current?.contains(e.target) ||
      selectRef.current?.contains(e.target)
    ) {
      return;
    }
    updateFocused(false);
  }

  /**
   * This method will handle the change event
   * @param {any} newValue
   */
  function onValueChange(newValue) {
    if (newValue !== value) {
      onChange(newValue);
    }

    if (unfocusOnChange) {
      updateFocused(false);
      selectRef.current?.focus();
    }
  }

  /**
   * This method will handle the select click event
   * @param {event} e
   */
  function onSelectClick(e) {
    if (disabled || optionsRef.current?.contains(e.target)) return;
    updateFocused(!focused);
  }

  /**
   * This method will keep the focus in the search input when the select is clicked while it's opened
   * @param {event} e
   */
  function onSelectMouseDown(e) {
    if (focused && searchRef.current && e.target !== searchRef.current) {
      e.preventDefault();
    }
  }

  /**
   * This method will close the options when the focus leaves the select and its options
   * @param {event} e
   */
  function onSelectBlur(e) {
    if (!focused) return;
    const target = e.relatedTarget;
    if (
      target &&
      (selectRef.current?.contains(target) ||
        optionsRef.current?.contains(target))
    ) {
      return;
    }
    updateFocused(false);
  }

  /**
   * This method will prevent the options from taking the focus when they are clicked
   * @param {event} e
   */
  function onOptionsMouseDown(e) {
    e.preventDefault();
  }

  /**
   * This method will highlight the option the mouse is moving over
   * @param {number} index
   */
  function onOptionHover(index) {
    if (index !== highlightedIndex && isSelectable(filteredOptions, index)) {
      setHighlightedIndex(index);
    }
  }

  /**
   * This method will handle the search change event
   * @param {event} e
   */
  function onSearchChange(e) {
    if (!isSearchable) return;
    updateSearch(e.target.value);
  }

  /**
   * This method will update the search and highlight the first matching option
   * @param {string} newSearch
   */
  function updateSearch(newSearch) {
    setSearch(newSearch);
    onSearchChanged?.(newSearch);

    const matches = newSearch
      ? sortedOptions.filter((option) =>
          StringHelper.matchesSearch(option.label, newSearch),
        )
      : sortedOptions;
    setHighlightedIndex(
      newSearch
        ? findSelectableIndex(matches, 0, 1)
        : getInitialHighlightIndex(matches),
    );
  }

  /**
   * This method will handle the key down event of the select and its search input
   * @param {event} e
   */
  function onKeyDown(e) {
    if (disabled) return;

    //Give the parent the chance to handle the key first
    onCustomKeyDown?.(e);
    if (e.defaultPrevented) return;

    if (!focused) {
      onClosedKeyDown(e);
    } else {
      onOpenedKeyDown(e);
    }
  }

  /**
   * This method will handle the key down event while the options are closed
   * @param {event} e
   */
  function onClosedKeyDown(e) {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case " ":
        e.preventDefault();
        updateFocused(true);
        return;
      default:
        break;
    }

    if (!isPrintableKey(e)) return;
    e.preventDefault();

    //Start searching with the typed character
    if (determineSearchable()) {
      updateFocused(true, e.key);
      return;
    }

    //Open the options and highlight the option that starts with the typed characters
    updateFocused(true);
    typeAhead(e.key, sortedOptions);
  }

  /**
   * This method will handle the key down event while the options are opened
   * @param {event} e
   */
  function onOpenedKeyDown(e) {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = findSelectableIndex(
          filteredOptions,
          highlightedIndex + 1,
          1,
        );
        if (next >= 0) setHighlightedIndex(next);
        return;
      }
      case "ArrowUp": {
        e.preventDefault();
        const previous = findSelectableIndex(
          filteredOptions,
          highlightedIndex - 1,
          -1,
        );
        if (previous >= 0) setHighlightedIndex(previous);
        return;
      }
      case "Home":
      case "End":
        //Keep the default behaviour of the search input
        if (isSearchable) return;
        e.preventDefault();
        setHighlightedIndex(
          e.key === "Home"
            ? findSelectableIndex(filteredOptions, 0, 1)
            : findSelectableIndex(
                filteredOptions,
                filteredOptions.length - 1,
                -1,
              ),
        );
        return;
      case "Enter":
        e.preventDefault();
        selectHighlightedOption();
        return;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        updateFocused(false);
        selectRef.current?.focus();
        return;
      case "Tab":
        //Move the focus to the select first, so the browser continues tabbing from there
        updateFocused(false);
        selectRef.current?.focus();
        return;
      default:
        break;
    }

    //Spaces are part of the search
    if (isSearchable) return;

    //Space selects the option, unless the user is typing ahead
    if (e.key === " " && !typeAheadRef.current.text) {
      e.preventDefault();
      selectHighlightedOption();
    } else if (isPrintableKey(e)) {
      e.preventDefault();
      typeAhead(e.key, filteredOptions);
    }
  }

  /**
   * This method will prevent a select that is rendered as a button from being clicked by the space key, because the key down event already handled it
   * @param {event} e
   */
  function onKeyUp(e) {
    if (e.key === " ") e.preventDefault();
  }

  /**
   * This method will select the highlighted option
   */
  function selectHighlightedOption() {
    if (isSelectable(filteredOptions, highlightedIndex)) {
      onValueChange(filteredOptions[highlightedIndex].value);
    }
  }

  /**
   * This method will highlight the first option that starts with the characters that were typed shortly after each other
   * @param {string} key
   * @param {array} list
   */
  function typeAhead(key, list) {
    const state = typeAheadRef.current;
    clearTimeout(state.timeout);
    state.text += key;
    state.timeout = setTimeout(() => {
      state.text = "";
    }, TYPE_AHEAD_TIMEOUT);

    const text = StringHelper.normalizeForSearch(state.text);
    const index = list.findIndex(
      (option) =>
        !option.disabled &&
        StringHelper.normalizeForSearch(option.label).startsWith(text),
    );
    if (index >= 0) setHighlightedIndex(index);
  }

  /**
   * This method will determine if the select should be searchable
   * @returns {boolean}
   */
  function determineSearchable() {
    if (searchable !== null) return searchable;
    return (
      sortedOptions.length >= 10 &&
      (selectRef.current?.getBoundingClientRect().width ?? 0) > 200
    );
  }

  /**
   * This method will update the focused state
   * @param {bool} value
   * @param {string} initialSearch the search to start with when the options are opened
   */
  function updateFocused(value, initialSearch = "") {
    setFocused(value);
    if (value) {
      setAutoSearchable(determineSearchable());
      if (initialSearch) {
        updateSearch(initialSearch);
      } else {
        setHighlightedIndex(getInitialHighlightIndex(sortedOptions));
      }
    } else {
      setHighlightedIndex(-1);
      if (search) {
        setSearch("");
        onSearchChanged?.("");
      }
    }
  }

  /**
   * This method will position the options below or above the select, and keep them positioned while the page scrolls or resizes
   * @returns {function|undefined} the cleanup function
   */
  function activateOptionPositioning() {
    //Don't position if the select is not focused
    if (!focused) return;

    //Get the select and options elements
    const select = selectRef.current;
    const options = optionsRef.current;
    if (!select || !options) return;

    /**
     * This method will position the options
     */
    function positionOptions() {
      //Get the rects
      const selectRect = select.getBoundingClientRect();
      const optionsRect = options.getBoundingClientRect();

      //Calculate the left and top positions for the options
      const left =
        selectRect.left + optionsRect.width > window.innerWidth
          ? window.innerWidth - optionsRect.width
          : selectRect.left;
      const top =
        selectRect.bottom + optionsRect.height + 2 > window.innerHeight
          ? selectRect.top - optionsRect.height - 2
          : selectRect.bottom + 2;
      const width = selectRect.width;

      //Set the options style
      options.style.left = `${left}px`;
      options.style.top = `${top}px`;
      options.style.minWidth = `${width}px`;
      options.style.maxWidth = `${window.innerWidth}px`;
    }

    positionOptions();

    //Reposition when any scroll container scrolls, the window resizes or the select or options change size
    window.addEventListener("scroll", positionOptions, true);
    window.addEventListener("resize", positionOptions);
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(positionOptions)
        : null;
    resizeObserver?.observe(select);
    resizeObserver?.observe(options);

    return () => {
      window.removeEventListener("scroll", positionOptions, true);
      window.removeEventListener("resize", positionOptions);
      resizeObserver?.disconnect();
    };
  }

  return (
    <SelectView
      id={id}
      value={selectedOption}
      search={search}
      searchable={isSearchable}
      disabled={disabled}
      focused={focused}
      options={filteredOptions}
      highlightedIndex={highlightedIndex}
      getOptionId={getOptionId}
      searchRef={searchRef}
      selectRef={selectRef}
      optionsRef={optionsRef}
      optionsPortal={getOptionsPortal()}
      onSearchChange={onSearchChange}
      onValueChange={onValueChange}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onSelectClick={onSelectClick}
      onSelectMouseDown={onSelectMouseDown}
      onSelectBlur={onSelectBlur}
      onOptionsMouseDown={onOptionsMouseDown}
      onOptionHover={onOptionHover}
      as={as}
      hideCaret={hideCaret}
      valueVisual={valueVisual}
      placeholder={placeholder}
      title={title}
      style={style}
    />
  );
}

export default Select;
